// BUILD-042 UAT local-usability cleanup (2026-10-05 decision), item #7 — plain-language,
// collapsible "How this page works" help. Explains capability vs. scope and confidentiality
// boundaries without changing the grant matrix, Owner authority, or server permissions — this
// component renders static explanatory text only, never a control that affects behavior.
// Critical consequences are never hidden solely inside this collapsed section; callers keep
// those visible in the page body itself (e.g. a destructive-action warning stays inline).
import { useState, type ReactNode } from 'react';

// UAT visual cleanup (2026-10-05, Owner-directed): "How this page works" moved from a small
// inline link sitting ABOVE a page's content to its own standalone card, placed BELOW that
// content — matching the same bg-white/rounded-lg/border card convention every other section on
// these HR screens already uses, so it reads as "one more card on the page" rather than a
// floating disclosure. Clicking the header still expands/collapses the explanation in place;
// nothing about WHAT it discloses or WHEN it is shown has changed, only where it sits and how it
// looks. Every caller of this shared component gets this automatically — see the pages listed in
// this file's own usages (Contact Details, Emergency Contacts, Audit, Access, Checklists,
// Employees) for where it was repositioned to the bottom of each page's content.
export function CollapsibleHelp({ title = 'How this page works', children }: { title?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-1.5 px-5 py-3 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
        {title}
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 space-y-2 border-t border-gray-100 pt-3">
          {children}
        </div>
      )}
    </div>
  );
}
